import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  EditingMode,
  getCrmFieldIdentityKey,
  getServiceIdFromIntegration,
  groupByAsMap,
  HistoryTransforms,
  isDynamicBlockDetails,
} from '@aline/common';
import type { FieldsInJSON } from '@aline/common';
import type { GridApi, GridFilterModel } from '@mui/x-data-grid-premium';
import { matchSorter } from 'match-sorter';
import { isEmpty } from 'ramda';
import { usePDFFieldAPI } from 'src/components';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useGetFieldsInJSON } from 'src/editor/editor-tab-state-registry';
import { useMaybeEditingHooks } from 'src/editor/helpers/useEditingHooks';
import { Apollo } from 'src/gql/generated';
import { useDispatchButtonSnack, useDocumentParams, useGetPDFFieldsByNodeId } from 'src/helpers';
import type { PDFField, PDFFieldsByNodeId } from 'src/helpers';
import {
  useAuthorizedTransforms,
  useGetApideckOpportunity,
  useGetCurrentDoc,
  useGetIntegrationOpportunities,
  useGetIsFieldInDocument,
  useGetVariablesForDocument,
} from 'src/hooks';
import type { LinkedOpportunitySubset } from 'src/hooks';

import { integrationFieldValueMatchesDocument } from './integration-field-sync';

interface FieldInDocInfo extends Pick<
  Apollo.DocumentVariable,
  'id' | 'label' | 'nodeId' | '__typename'
> {
  assigneeId: UUID | null;
  integration: Apollo.Integration | null;
  isStale: boolean;
}
export interface DocumentCRMField extends Apollo.IntegrationField {
  fieldInDocInfo: FieldInDocInfo | null;
}

export type FieldGroup = {
  readonly fields: DocumentCRMField[];
  readonly isCustomObject: boolean;
  readonly label: string;
  readonly sortIndex: number;
};

/**
 * Identity of a rendered picker group. Fields that share a label and a position (HubSpot's
 * unified Opportunity rows and native deal rows both arrive as "Deal" at index 1) fold into
 * one group; the sort index keeps two same-label groups at different positions apart and
 * gives React a key that follows display order.
 */
export function getFieldGroupKey(group: Pick<FieldGroup, 'label' | 'sortIndex'>) {
  return `${group.sortIndex}:${group.label}`;
}

// Fields matching the search text, bucketed into picker groups in display order.
export function getFieldGroups(fields: DocumentCRMField[], searchText: string): FieldGroup[] {
  const matched = matchSorter(fields, searchText, { keys: ['title'] });
  const groupsByKey = groupByAsMap(field => {
    return getFieldGroupKey({ label: field.groupDisplayName, sortIndex: field.groupSortIndex });
  }, matched);
  return [...groupsByKey.values()]
    .flatMap(groupFields => {
      const first = groupFields.at(0);
      if (!first) return [];
      return [
        {
          fields: groupFields,
          isCustomObject: first.category === Apollo.CrmIntegrationCategory.CustomObject,
          label: first.groupDisplayName,
          sortIndex: first.groupSortIndex,
        },
      ];
    })
    .sort(
      (groupA, groupB) =>
        groupA.sortIndex - groupB.sortIndex || groupA.label.localeCompare(groupB.label)
    );
}

// The slice of a document variable that the "Added to template" match reads.
interface DocumentIntegrationVariable extends Pick<
  Apollo.DocumentVariable,
  '__typename' | 'id' | 'label' | 'nodeId' | 'value'
> {
  readonly action: { readonly assignee: { readonly id: UUID } | null } | null;
  readonly integrationLink: {
    readonly attributePath: string | null;
    readonly integration: Apollo.Integration | null;
  } | null;
}

/**
 * Catalog fields with the document variable bound to each one, matched by CRM identity
 * so HubSpot `opportunity.title` and `native.deals.dealname` count as the same field.
 * Variables bound to another CRM, or no longer present in the document, do not match.
 */
export function getDocumentIntegrationFields({
  fieldsInJSON,
  getIsFieldInDocument,
  integration,
  integrationFields,
  pdfFieldsByNodeId,
  variables,
}: {
  readonly fieldsInJSON: Pick<FieldsInJSON, 'inChildren'>;
  readonly getIsFieldInDocument: ReturnType<typeof useGetIsFieldInDocument>['getIsFieldInDocument'];
  readonly integration: Apollo.Integration;
  readonly integrationFields: readonly Apollo.IntegrationField[];
  readonly pdfFieldsByNodeId: PDFFieldsByNodeId;
  readonly variables: readonly DocumentIntegrationVariable[];
}): DocumentCRMField[] {
  const fieldIdentityKey = getCrmFieldIdentityKey(integration);
  const variablesByFieldIdentity = new Map<string, DocumentIntegrationVariable>();
  for (const variable of variables) {
    const attributePath = variable.integrationLink?.attributePath;
    if (
      attributePath &&
      variable.integrationLink?.integration === integration &&
      (Boolean(fieldsInJSON.inChildren[variable.nodeId]) || getIsFieldInDocument(variable))
    ) {
      const key = fieldIdentityKey(attributePath);
      if (!variablesByFieldIdentity.has(key)) variablesByFieldIdentity.set(key, variable);
    }
  }

  return integrationFields.map<DocumentCRMField>(integrationField => {
    const matchingVariable = variablesByFieldIdentity.get(fieldIdentityKey(integrationField.path));
    if (!matchingVariable) return { ...integrationField, fieldInDocInfo: null };

    const documentText = getDocumentText({
      fieldDetails: fieldsInJSON.inChildren[matchingVariable.nodeId],
      pdfField: pdfFieldsByNodeId.get(matchingVariable.nodeId),
      variable: matchingVariable,
    });

    return {
      ...integrationField,
      fieldInDocInfo: {
        __typename: 'DocumentVariable',
        assigneeId: matchingVariable.action?.assignee?.id ?? null,
        id: matchingVariable.id,
        integration,
        isStale: !integrationFieldValueMatchesDocument(documentText, integrationField.value, [
          integrationField.title,
          matchingVariable.label,
        ]),
        label: matchingVariable.label,
        nodeId: matchingVariable.nodeId,
      },
    };
  });
}

export function useGetIntegrationFieldsFromLinkedOpp({
  integration,
  linkedOpportunity,
  workflowId,
}: {
  integration: Apollo.Integration;
  linkedOpportunity: LinkedOpportunitySubset | null;
  workflowId: UUID;
}) {
  const { loading: loadingTemplateIntegrationFields, templateIntegrationFields } =
    useTemplateIntegrationFields(integration);
  const {
    apiDeckOpportunity,
    loading: loadingApideckOpp,
    recordDeleted,
  } = useGetApideckOpportunity(linkedOpportunity, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    workflowId,
  });
  const { documentIntegrationFields } = useDocumentIntegrationFields({
    integration,
    integrationFields: apiDeckOpportunity?.fields ?? templateIntegrationFields,
  });

  const loading = linkedOpportunity
    ? loadingApideckOpp && !apiDeckOpportunity
    : loadingTemplateIntegrationFields && templateIntegrationFields.length === 0;

  return {
    apiDeckOpportunity,
    documentIntegrationFields,
    loading,
    recordDeleted,
  };
}

export function useDocumentIntegrationFields({
  integration,
  integrationFields,
}: {
  integration: Apollo.Integration;
  integrationFields: Apollo.IntegrationField[];
}): {
  documentIntegrationFields: DocumentCRMField[];
} {
  const { documentId } = useDocumentParams();
  const { fieldsInJSON } = useGetFieldsInJSON();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const pdfFieldsByNodeId = useGetPDFFieldsByNodeId(tabId);
  const { getIsFieldInDocument } = useGetIsFieldInDocument(tabId);
  const { variables } = useGetVariablesForDocument(documentId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const documentIntegrationFields = useMemo(
    () =>
      getDocumentIntegrationFields({
        fieldsInJSON,
        getIsFieldInDocument,
        integration,
        integrationFields,
        pdfFieldsByNodeId,
        variables,
      }),
    [
      fieldsInJSON,
      getIsFieldInDocument,
      integration,
      integrationFields,
      pdfFieldsByNodeId,
      variables,
    ]
  );

  return { documentIntegrationFields };
}

export function useUpdateIntegrationFieldsInDoc() {
  const { document } = useGetCurrentDoc();
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { setPDFFieldValue } = usePDFFieldAPI(documentViewer, tabId);
  const { currentUserId, editingMode, editor } = useMaybeEditingHooks();
  const isPDF = document?.latestVersion.__typename === 'StaticFileVersion';
  const dispatchSuccessSnack = useDispatchButtonSnack({
    autoHideDurationMS: 7500,
    buttonText: 'Undo',
    message: 'Document updated successfully.',
    onClick: e => {
      e.stopPropagation();
      if (editor) HistoryTransforms.undo(editor);
    },
    variant: 'success',
  });
  const updateIntegrationFieldsInDoc = useCallback(
    (integrationFields: DocumentCRMField[]) => {
      const data = integrationFields.reduce<Record<string, string>>((acc, integrationField) => {
        if (!integrationField.fieldInDocInfo) return acc;

        acc[integrationField.fieldInDocInfo.nodeId] = integrationField.value ?? '';
        return acc;
      }, {});
      if (isEmpty(data)) return {};

      // PDF fields live in the PDFTron field manager, not the Slate editor.
      // Update them directly via setPDFFieldValue instead of AuthorizedTransforms.
      if (isPDF) {
        Object.entries(data).forEach(([nodeId, value]) => setPDFFieldValue(nodeId, value));
        dispatchSuccessSnack();
        return data;
      }

      if (!currentUserId || !editingMode || !editor) return {};

      const updatedRecord = AuthorizedTransforms.updateInputFieldsWithData(editor, {
        // Anyone can update the integration field to sync w/ CRM.
        assigneeId: null,
        authorId: currentUserId,
        data,
        editingMode: EditingMode.EDITING,
      });
      if (!isEmpty(updatedRecord)) dispatchSuccessSnack();

      return updatedRecord;
    },
    [
      AuthorizedTransforms,
      currentUserId,
      dispatchSuccessSnack,
      editingMode,
      editor,
      isPDF,
      setPDFFieldValue,
    ]
  );

  return { updateIntegrationFieldsInDoc };
}

export function useGetOpportunitiesWithPagination(args: {
  apiRef: React.RefObject<GridApi | null>;
  integration: Apollo.Integration;
}) {
  const { apiRef, integration } = args;
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const { fetchMore, loading, nextCursor, opportunities } = useGetIntegrationOpportunities({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      cursor: null,
      filter: { title: filterModel?.quickFilterValues?.join(' ') ?? '' },
      limit: 25,
      serviceId: getServiceIdFromIntegration(integration),
    },
  });
  const fetchMoreOpportunities = useCallback(async () => {
    if (nextCursor)
      await fetchMore({
        variables: { cursor: nextCursor },
      });
  }, [fetchMore, nextCursor]);

  useEffect(() => {
    const cleanupFilterModelChange = apiRef.current?.subscribeEvent(
      'filterModelChange',
      filterModel => setFilterModel(filterModel)
    );

    return () => {
      cleanupFilterModelChange?.();
    };
  }, [apiRef]);

  return { fetchMoreOpportunities, loading, opportunities };
}

export function useTemplateIntegrationFields(integration: Apollo.Integration) {
  const { data, loading } = Apollo.useTemplateOpportunityAttributesQuery({
    variables: { serviceId: getServiceIdFromIntegration(integration) },
  });
  const templateIntegrationFields = useMemo(() => {
    return (
      data?.templateOpportunityAttributes.map<DocumentCRMField>(integrationField => ({
        ...integrationField,
        fieldInDocInfo: null,
      })) ?? []
    );
  }, [data?.templateOpportunityAttributes]);

  return {
    loading,
    templateIntegrationFields,
  };
}

/**
 * What the document currently shows for a bound variable. DOCX reads the field text from
 * the Slate JSON, PDF reads the live value from the PDFTron field manager. The stored
 * variable value is the last resort; for PDFs it can lag behind what the viewer shows.
 */
function getDocumentText({
  fieldDetails,
  pdfField,
  variable,
}: {
  readonly fieldDetails: FieldsInJSON['inChildren'][string] | undefined;
  readonly pdfField: PDFField | undefined;
  readonly variable: Pick<DocumentIntegrationVariable, 'value'>;
}) {
  if (fieldDetails && !isDynamicBlockDetails(fieldDetails)) return fieldDetails.text;
  if (pdfField?.widgets?.length) return String(pdfField.getValue());
  return variable.value;
}
